import { breakpoints } from '@/theme/responsive'
import { useEffect, useLayoutEffect, useState } from 'react'

export type Viewport = 'mobile' | 'tablet' | 'desktop'

/**
 * Hook that returns the current viewport category based on screen width.
 *
 * mobile: < 600px
 * tablet: 600px - 768px (inclusive)
 * desktop: > 768px
 *
 * @returns Current viewport category
 */
export const useViewport = (): Viewport => {
  const [viewport, setViewport] = useState<Viewport>(() => getViewport())

  // Use useLayoutEffect to update viewport synchronously before paint
  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    setViewport(getViewport())
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const tabletQuery = window.matchMedia('(min-width: 600px)')
    const desktopQuery = window.matchMedia(`(min-width: ${breakpoints.md + 1}px)`)

    const updateViewport = (): void => {
      setViewport(getViewport())
    }

    tabletQuery.addEventListener('change', updateViewport)
    desktopQuery.addEventListener('change', updateViewport)

    return () => {
      tabletQuery.removeEventListener('change', updateViewport)
      desktopQuery.removeEventListener('change', updateViewport)
    }
  }, [])

  return viewport
}

const getViewport = (): Viewport => {
  if (typeof window === 'undefined') {
    return 'desktop'
  }

  const width = window.innerWidth

  // Thresholds:
  // - mobile: < 600px (phones)
  // - tablet: 600px - 768px inclusive
  // - desktop: > 768px
  const MOBILE_MAX = 600

  if (width > breakpoints.md) {
    return 'desktop'
  }
  if (width >= MOBILE_MAX) {
    return 'tablet'
  }
  return 'mobile'
}
